// #region Imports
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { styled } from "@mui/material/styles";
import { scrollShadows } from "_shared/_views/scroll-shadows";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleTeamMemberPickerField from "_shared/fee-schedules/categories/_views/fee-schedule-team-member-picker-field";
import {
  focusRequestFor,
  type FeeScheduleCategoryEditorFocus,
  useFocusWhenAsked,
} from "_shared/fee-schedules/categories/_views/fee-schedule-category-editor-focus";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import {
  FeeScheduleCategoryType,
  feeScheduleCategoryTypeLabel,
} from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import Guid from "_shared/guids/guid";
import CurrencyInput from "_shared/money/currencies/_views/currency-input";
import { Currency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule } from "work/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";
// #endregion

// #region Styled Components
const FormContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
}));
// The buttons stay put under the fields, which scroll between them and the title. A phone draws no
// scrollbar, so the box shadows the edge that has more past it (#7004).
const FormFields = styled("div")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  ...scrollShadows(theme),
}));
const NameTextField = styled(TextField)(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const DescriptionTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
/**
 * Below this width the fee row stacks: the amount and its currency on one line, "per" and the unit on
 * the next. Side by side, the amount and currency need about 210px and the unit about 130, so a panel
 * at its 320px floor, or a phone's drawer, ran the currency picker under the slash and left no room for
 * an amount in the thousands.
 */
const FEE_ROW_STACKS_BELOW = 400;
const FEE_FIELDSET_CONTAINER = "fee-fieldset";
const feeRowIsStacked = `@container ${FEE_FIELDSET_CONTAINER} (max-width: ${FEE_ROW_STACKS_BELOW - 0.05}px)`;

/** The slash between the amount and the unit, and the word that stands for it once the row stacks. */
const PerSymbol = styled("span")(({ theme }) => ({
  alignSelf: "center",
  fontSize: "2em",
  lineHeight: "1em",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  "& .per-word": { display: "none" },
  [feeRowIsStacked]: {
    ...theme.typography.body1,
    color: theme.palette.text.secondary,
    paddingLeft: 0,
    "& .per-slash": { display: "none" },
    "& .per-word": { display: "inline" },
  },
}));
/**
 * Fee or expense: one required choice of two, so both are on screen at once rather than behind a
 * select. It took the place of the optional Billing Code field (#6882).
 */
const TypeFieldset = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
})) as typeof FormControl;
const TYPE_OPTIONS = [FeeScheduleCategoryType.Fee, FeeScheduleCategoryType.Expense] as const;
/**
 * The fee's own group: whether the other side sets it, and if not, its shape and its amount. A
 * fieldset, so the two checkboxes are read as questions about the fee rather than about the category.
 */
const FeeFieldset = styled(FormControl)(() => ({
  containerName: FEE_FIELDSET_CONTAINER,
  containerType: "inline-size",
  display: "flex",
  width: "100%",
})) as typeof FormControl;
const FeeLegend = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightMedium,
  "&.Mui-focused": {
    color: theme.palette.text.primary,
  },
})) as typeof FormLabel;
const DeferredFeeNote = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
})) as typeof Typography;
const TeamMemberField = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(3),
}));
/**
 * Rate, per, unit — on one row, and able to shrink to the panel it is drawn in.
 *
 * `minmax(0, …)` rather than a bare `2fr auto 1fr`: an `fr` track's floor is `auto`, so the tracks
 * refused to go below their contents' own minimum widths and the row stayed the width it wanted
 * whatever the panel could spare. This editor is drawn in a side panel that is `clamp(320px, 22vw,
 * 400px)` wide, so on any window below about 1650px the row was wider than the column it is in and
 * the panel scrolled sideways — with the unit field's own control hanging off the right-hand edge.
 *
 * `side-panel-width` puts the panel's 320px floor at exactly "the width where the category editor's
 * two-column money fields still fit side by side". This is what makes that true.
 */
const FeeRate = styled("div", {
  shouldForwardProp: (prop) => prop !== "isFixedFee",
})<{ isFixedFee?: boolean }>(({ isFixedFee, theme }) => ({
  display: "grid",
  // A fixed fee is an amount and nothing per anything, so it has the row to itself. Otherwise the
  // unit gets two fifths: at a third, the panel's 320px floor cut "Hour" to "H…" beside its clear button.
  gridTemplateColumns: isFixedFee ? "minmax(0, 1fr)" : "minmax(0, 3fr) auto minmax(0, 2fr)",
  marginTop: theme.spacing(1),
  [feeRowIsStacked]: {
    gridTemplateColumns: isFixedFee ? "minmax(0, 1fr)" : "auto minmax(0, 1fr)",
    rowGap: theme.spacing(1.5),
    // The amount, and any error about it, take the whole first line.
    "& > :first-child, & > .MuiFormHelperText-root": { gridColumn: "1 / -1" },
  },
}));
/**
 * The unit field asks for 8rem and settles for its share of the row.
 *
 * It used to hold 8rem as a hard floor and give it up only under `breakpoints.down("sm")` — a
 * question about the *window*, asked by a control whose width comes from a panel that is 320px wide
 * on a 1440px display. The window was never below `sm`, so the floor never lifted; this is #4964's
 * mistake in miniature. The preferred width stays, as a width the track hands over when it has it.
 */
const UnitAutocomplete = styled(Autocomplete)(() => ({
  minWidth: 0,
  width: "8rem",
  maxWidth: "100%",
  "& .MuiInputBase-root": {
    width: "100%",
  },
  [feeRowIsStacked]: {
    width: "100%",
  },
}));
/** Add and Cancel, held below the scrolling fields. */
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  flexShrink: 0,
  marginTop: "auto",
  paddingTop: theme.spacing(3),
}));
const ButtonTooltipTarget = styled("span")(() => ({
  display: "grid",
}));
const UNIT_OPTIONS = ["Hour", "Day", "Week", "Month", "Year", "Each"] as const;
const DESCRIPTION_MIN_ROWS = 3;
// #endregion

// #region Types
type EditableFeeScheduleCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
type SelectedFeeScheduleCategories = EditableFeeScheduleCategory[] | MaskedFeeSchedule;
type TeamMemberOption = { member: EntityMember; label: string };

type EditFeeScheduleCategoryProps = {
  category?: EditableFeeScheduleCategory;
  onCancel?: () => void;
  onCategoryAdded?: (category: EditableFeeScheduleCategory) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    category: EditableFeeScheduleCategory,
  ) => void;
  selectedCategories?: SelectedFeeScheduleCategories;
  /**
   * Whether this surface says who a rate is for. Defaults to false, and nothing is drawn.
   *
   * An explicit flag rather than the presence of `teamMembers`, which is what used to turn the field
   * on — so an empty list drew a field with nothing in it, and a surface with a picker and no list
   * had to invent one.
   */
  offersTeamMember?: boolean;
  /**
   * The people a rate may name, where that list is the whole of the answer and the field is a
   * choice from it. Read only when `onBrowseForTeamMember` is not given.
   *
   * Only a marketplace team's published rates are in that position: the API rejects anyone outside
   * the team, so the team's own roster is exactly the set of valid answers there.
   */
  teamMembers?: readonly EntityMember[];
  /**
   * Whether the fee may be left for the other side to set. Defaults to true, which is what a work
   * agreement or a proposal wants: a rate the two parties have not agreed yet is a real state there.
   *
   * A marketplace team's published rates have no other side to defer to, and the API contract has
   * no way to say "no fee" — `FeeScheduleCategoryCommand.FeeRate` is a plain `decimal`, so an
   * omitted rate binds to `0` and persists as a $0 fixed fee. Callers in that position pass false.
  */
  allowDeferredFee?: boolean;
  /**
   * Whether somebody may be named whatever shape the fee takes. Defaults to false.
   *
   * On a proposal, a work agreement or a team's published rates, the person named on an hourly rate
   * is the person who will bill it, and only a rate that has a price to bill can name one.
   *
   * A fee schedule TEMPLATE is a rate card and not an engagement. It says who usually does this
   * work, which is worth recording whatever shape the fee takes: a template rate that leaves the
   * price to the other side still has somebody in mind for it. So callers in that position pass
   * true, and the field stays offered for a fixed or deferred fee.
   */
  teamMemberOnEveryFee?: boolean;
  /**
   * Whether an hourly rate with a price must name who bills it before this form takes it. Defaults
   * to false.
   *
   * Only a marketplace team's published rates pass true: the team is the whole of who bills them,
   * so a rate it publishes says who (#4489). A template and a proposal may leave the rate open,
   * because who bills it depends on the team the proposal assembles. The proposal asks when it is
   * approved or hired, where `WorkAgreement.ValidateFeeSchedule` refuses an hourly rate that names
   * nobody (#6605).
   */
  hourlyRateNamesItsBiller?: boolean;
  /**
   * Opens the full picker, resolving to whoever was chosen, or to undefined when it was dismissed.
   * Given, the field is the chosen person's name, a way to choose somebody else and a clear action —
   * nothing to type. `EditFeeScheduleCategoryDrawer` opens it in its own panel.
   *
   * A template and a proposal both pass it. A template's rate may name anyone its author would put
   * on a team. A proposal offers `teamRoster` first, and adds anybody chosen here to its vendor team
   * when the rate is saved — `WorkAgreement.ValidateFeeSchedule` holds that line at hire time.
   */
  onBrowseForTeamMember?: () => Promise<EntityMember | undefined>;
  /**
   * A proposal's vendor team, which the field offers before the full picker (#5798). A template has
   * no team and passes none.
   */
  teamRoster?: FeeScheduleTeamRoster;
  /**
   * Asks for a field to take the focus: the Team Member or the Fee, and each new request is one more
   * ask (#6097). A page passes one when the reader pressed an issue about that part of the rate.
   */
  focus?: FeeScheduleCategoryEditorFocus;
  fieldErrors?: Readonly<Record<string, string>>;
};

export default function EditFeeScheduleCategory(props: Readonly<EditFeeScheduleCategoryProps>) {
  const { category, onCancel, onCategoryAdded, onCategoryUpdated, selectedCategories, teamMembers, fieldErrors } = props;
  const { onBrowseForTeamMember, teamRoster } = props;
  const teamMemberAssignmentEnabled = props.offersTeamMember ?? false;
  const picksTeamMember = teamMemberAssignmentEnabled && onBrowseForTeamMember !== undefined;
  const allowDeferredFee = props.allowDeferredFee ?? true;
  const teamMemberOnEveryFee = props.teamMemberOnEveryFee ?? false;
  const hourlyRateNamesItsBiller = props.hourlyRateNamesItsBiller ?? false;
  const feeFieldsetRef = useRef<HTMLFieldSetElement>(null);
  useFocusWhenAsked(feeFieldsetRef, focusRequestFor(props.focus, "fee"));

  // #region State
  const [formDirty, setFormDirty] = useState(false);
  const [formName, setFormName] = useState<string>(category?.name?.value ?? "");
  const [formDescription, setFormDescription] = useState<string>(category?.description?.value ?? "");
  // A new category has no type until the actor chooses one: the choice is required, and a default would
  // let a cost go through as a fee because nobody looked at the question.
  const [formType, setFormType] = useState<FeeScheduleCategoryType | null>(category?.type ?? null);

  // Where deferral is not on offer there is no such thing as a rate nobody set, so a category that
  // arrives without one opens with the field empty and the form invalid until it is filled in —
  // rather than with the zero that would otherwise save as a $0 fixed fee.
  const [formFeeRate, setFormFeeRate] = useState<Money | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency
      ? undefined
      : (category?.fee?.rate ?? (allowDeferredFee ? new Money(0, Currency.USD) : undefined)),
  );
  const [formFeeUnit, setFormFeeUnit] = useState<string | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : (category?.fee?.unit ?? ""),
  );
  const [isFixedFee, setIsFixedFee] = useState<boolean | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : Boolean(category && !category.fee?.unit),
  );
  // A category that arrived deferred opens un-deferred where deferral is not on offer, so its rate
  // has to be filled in before it can be saved.
  const [isDeferred, setIsDeferred] = useState<boolean | undefined>(
    category instanceof FeeScheduleCategoryMaskedCurrency ? undefined : allowDeferredFee && !category?.fee,
  );
  const [formTeamMember, setFormTeamMember] = useState<EntityMember | null>(
    teamMemberAssignmentEnabled ? (category?.teamMember ?? null) : null,
  );
  // #endregion

  function getFieldError(field: string): string | undefined {
    if (!fieldErrors) return undefined;
    const normalizedField = field.replace(/[._\-\s]/g, "").toLowerCase();
    const entry = Object.entries(fieldErrors).find(
      ([key]) => key.replace(/[._\-\s]/g, "").toLowerCase() === normalizedField,
    );
    return entry?.[1];
  }

  // The one rule this editor can enforce about staffing, where the caller asks for it: an hourly fee
  // names who bills it. An expense bills nobody's time, whatever its unit (#6883). Read once, because
  // the field's `required`, its helper text and `formValid` must not be able to disagree.
  const teamMemberIsRequired =
    teamMemberAssignmentEnabled &&
    hourlyRateNamesItsBiller &&
    formType === FeeScheduleCategoryType.Fee &&
    !isDeferred &&
    !isFixedFee &&
    Fee.isHourlyUnit(formFeeUnit);

  // #region Hooks
  const session = useSession();
  // The list and its names serve the list field alone. The picker field looks up the one person it
  // names, whoever they are.
  const availableTeamMembers = useMemo(
    () => (picksTeamMember ? [] : (teamMembers ?? [])),
    [picksTeamMember, teamMembers],
  );
  const teamMemberUserIds = useMemo(
    () => new Set(availableTeamMembers.map((member) => member.userId.value)),
    [availableTeamMembers],
  );
  const individualsQuery = useGetIndividualsByUserIdsQuery(
    teamMemberAssignmentEnabled && !picksTeamMember && teamMemberUserIds.size > 0
      ? { userIds: [...teamMemberUserIds] }
      : skipToken,
  );
  const teamMemberOptions = useMemo<TeamMemberOption[]>(() => {
    const namesByUserId = new Map(
      (Individual.fromJSONArray(individualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
        individual.userId.value,
        individual.fullName,
      ]),
    );
    const members = [...availableTeamMembers];
    if (formTeamMember && !members.some((member) => member.isEqualTo(formTeamMember))) members.push(formTeamMember);
    return members.map((member) => ({
      member,
      label: namesByUserId.get(member.userId.value) ?? "Team member",
    }));
  }, [availableTeamMembers, formTeamMember, individualsQuery.data]);

  // Whether naming somebody means anything for this rate. Where it is a billing assignment, only an
  // hourly-style rate with a price can carry one: a fee the other side sets has nothing to bill yet,
  // and a fixed fee bills nobody's hours. A rate card may say who does the work however its fee is
  // expressed.
  //
  // Where it means nothing the field is not drawn — rather than drawn disabled, which left the actor
  // hunting for what would switch it back on — and nobody is saved with the rate, so what is saved is
  // what was on screen. Masked staffing is the exception: it is somebody else's to change, so it stays,
  // shown disabled, whatever the fee does. So is a server error about the team member, which must
  // never point at a field that is not there.
  const teamMemberIsMasked = Boolean(category?.teamMemberIsMasked);
  const teamMemberApplies = teamMemberOnEveryFee || (!isDeferred && !isFixedFee);
  const showsTeamMember =
    teamMemberAssignmentEnabled && (teamMemberApplies || teamMemberIsMasked || Boolean(getFieldError("team")));
  const teamMemberDisabled = teamMemberIsMasked;

  function handleTeamMemberChanged(teamMember: EntityMember | null) {
    setFormTeamMember(teamMember);
    setFormDirty(true);
  }

  // #region Functions
  function formValid(): boolean {
    if (category instanceof FeeScheduleCategoryMaskedCurrency) {
      return Boolean(formName.trim() && formDescription.trim() && formType);
    }
    return Boolean(
      formName.trim() &&
      formDescription.trim() &&
      formType &&
      (isDeferred ||
        (formFeeRate &&
          (isFixedFee ||
            (formFeeUnit &&
              formFeeUnit.trim().length > 0 &&
              (!teamMemberIsRequired || formTeamMember))))),
    );
  }

  function handleAddCategoryClicked(): void {
    const newCategory = getStateAsFeeScheduleCategory();
    if (!newCategory) return;
    onCategoryAdded?.(newCategory);
  }

  function handleSaveCategoryClicked(originalName: FeeScheduleCategoryName | null): void {
    if (!category) return;

    const updatedCategory = getStateAsFeeScheduleCategory();
    if (!updatedCategory) return;
    onCategoryUpdated?.(originalName, updatedCategory);
  }

  function getStateAsFeeScheduleCategory(): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined {
    if (!formName.trim() || !formDescription.trim() || !formType) return;
    const normalizedName = formName.trim();
    const normalizedDescription = formDescription.trim();
    const normalizedUnit = formFeeUnit?.trim() ?? "";
    if (category instanceof FeeScheduleCategoryMaskedCurrency) {
      return new FeeScheduleCategoryMaskedCurrency({
        id: category.id,
        name: new FeeScheduleCategoryName(normalizedName),
        description: new FeeScheduleCategoryDescription(normalizedDescription),
        type: formType,
        teamMember: showsTeamMember ? formTeamMember : null,
        teamMemberIsMasked: teamMemberAssignmentEnabled && category.teamMemberIsMasked,
        version: category.version,
      });
    }
    if (formFeeRate === undefined || formFeeUnit === undefined || isFixedFee === undefined || isDeferred === undefined)
      return;
    let fee: Fee | null;
    if (isDeferred) {
      fee = null;
    } else if (isFixedFee) {
      fee = Fee.create({ rate: formFeeRate, unit: null });
    } else {
      if (!normalizedUnit) return;
      fee = Fee.create({ rate: formFeeRate, unit: normalizedUnit });
    }

    return new FeeScheduleCategory({
      name: new FeeScheduleCategoryName(normalizedName),
      description: new FeeScheduleCategoryDescription(normalizedDescription),
      fee,
      type: formType,
      teamMember: showsTeamMember ? formTeamMember : null,
      teamMemberIsMasked: teamMemberAssignmentEnabled && category?.teamMemberIsMasked,
      id: category?.id ?? Guid.generate(),
      version: category?.version,
    });
  }

  function getSaveDisabledTooltip(nameAlreadyExists: boolean): string {
    // Context-neutral, because this editor is now also reached from a marketplace team's profile
    // and from a work agreement, where there is no proposal to name.
    if (nameAlreadyExists) return "This category already exists in this fee schedule.";
    if (!formType) return "Choose whether this category is a fee or an expense.";
    if (feeRateIsMissing()) return "Set a rate for this category.";
    return "";
  }

  function feeRateIsMissing(): boolean {
    if (category instanceof FeeScheduleCategoryMaskedCurrency) return false;
    return !isDeferred && formFeeRate === undefined;
  }

  // Worded from the reader's own side: the other party is who would set it. The label stays put when
  // it is ticked — it used to turn into "Vendor to set fee", which is a statement, on a checkbox — and
  // the note that replaces the fee fields says what ticking it did.
  const otherParty = session.context?.viewingAsVendor ? "client" : "vendor";

  function categoryHasChanges(): boolean {
    if (!category) return formDirty;

    const updatedCategory = getStateAsFeeScheduleCategory();
    if (!updatedCategory) return false;

    if (
      category instanceof FeeScheduleCategoryMaskedCurrency &&
      updatedCategory instanceof FeeScheduleCategoryMaskedCurrency
    ) {
      return !feeScheduleCategoryBaseFieldsAreEqual(category, updatedCategory);
    }

    if (category instanceof FeeScheduleCategory && updatedCategory instanceof FeeScheduleCategory) {
      return !(
        feeScheduleCategoryBaseFieldsAreEqual(category, updatedCategory) &&
        ((!category.fee && !updatedCategory.fee) ||
          Boolean(
            category.fee &&
            updatedCategory.fee &&
            category.fee.rate.isEqualTo(updatedCategory.fee.rate) &&
            (category.fee.unit || null) === (updatedCategory.fee.unit || null),
          )) &&
        (!teamMemberAssignmentEnabled ||
          (((!category.teamMember && !updatedCategory.teamMember) ||
            category.teamMember?.isEqualTo(updatedCategory.teamMember)) &&
            category.teamMemberIsMasked === updatedCategory.teamMemberIsMasked))
      );
    }

    return true;
  }

  function categoryNameAlreadyExistsInThisFeeSchedule(): boolean {
    if (!formName.trim() || Masked.isMasked(selectedCategories)) return false;

    const formCategoryName = new FeeScheduleCategoryName(formName.trim());
    return selectedCategories?.some((selectedCategory) => {
      if (category?.id.isEqualTo(selectedCategory.id)) return false;
      return selectedCategory.name.isEqualTo(formCategoryName);
    }) ?? false;
  }

  function feeScheduleCategoryBaseFieldsAreEqual(
    originalCategory: EditableFeeScheduleCategory,
    updatedCategory: EditableFeeScheduleCategory,
  ): boolean {
    return Boolean(
      originalCategory.name.isEqualTo(updatedCategory.name) &&
      originalCategory.description?.isEqualTo(updatedCategory.description ?? null) &&
      originalCategory.type === updatedCategory.type,
    );
  }

  // #region Render
  const feeControlsDisabled = category instanceof FeeScheduleCategoryMaskedCurrency;
  const categoryNameAlreadyExists = categoryNameAlreadyExistsInThisFeeSchedule();
  const saveDisabled = categoryNameAlreadyExists || !categoryHasChanges() || !formValid();
  const saveDisabledTooltip = getSaveDisabledTooltip(categoryNameAlreadyExists);

  return (
    <FormContainer>
      <FormFields>
        <NameTextField
          label="Category"
          sx={{ marginTop: 1 }}
          value={formName}
          required
          error={Boolean(getFieldError("category"))}
          helperText={getFieldError("category")}
          onChange={(event) => {
            setFormName(event.target.value);
            setFormDirty(true);
          }}
        />
        <DescriptionTextField
          variant="outlined"
          label="Description Of Work"
          value={formDescription}
          multiline
          // Three lines to start, growing as it is typed into. Five held 100px of a 320px-wide panel
          // empty, and on a 1280×720 window that was the room the Fee section needed (#5809).
          minRows={DESCRIPTION_MIN_ROWS}
          required
          error={Boolean(getFieldError("description"))}
          helperText={getFieldError("description")}
          onChange={(event) => {
            setFormDescription(event.target.value);
            setFormDirty(true);
          }}
        />
        <TypeFieldset component="fieldset" required error={Boolean(getFieldError("type"))}>
          <FeeLegend component="legend">Type</FeeLegend>
          <RadioGroup
            row
            name="fee-schedule-category-type"
            value={formType ?? ""}
            onChange={(event) => {
              setFormType(event.target.value as FeeScheduleCategoryType);
              setFormDirty(true);
            }}
          >
            {TYPE_OPTIONS.map((type) => (
              <FormControlLabel
                key={type}
                value={type}
                label={feeScheduleCategoryTypeLabel(type)}
                control={<Radio color="primary" />}
              />
            ))}
          </RadioGroup>
          {getFieldError("type") && <FormHelperText>{getFieldError("type")}</FormHelperText>}
        </TypeFieldset>
        <FeeFieldset component="fieldset" ref={feeFieldsetRef}>
          <FeeLegend component="legend">Fee</FeeLegend>
          {allowDeferredFee && (
            <FormControlLabel
              label={`Let ${otherParty} set charge?`}
              control={
                <Checkbox
                  color="primary"
                  checked={Boolean(isDeferred)}
                  disabled={category instanceof FeeScheduleCategoryMaskedCurrency}
                  onChange={() => {
                    setIsDeferred((prevValue) => !prevValue);
                    setFormDirty(true);
                  }}
                />
              }
            />
          )}
          {/*
            A fee the other side sets has no shape or amount yet, so the controls for them are not
            drawn, rather than drawn disabled. Their values are kept, and come back as they were if the
            box is unticked. A masked fee keeps them, disabled: there IS an amount, it is not this
            reader's to see.
          */}
          {isDeferred ? (
            <DeferredFeeNote variant="body2">
              The {otherParty} will set this charge.
            </DeferredFeeNote>
          ) : (
            <>
              <FormControlLabel
                label="Fixed charge"
                labelPlacement="end"
                control={
                  <Checkbox
                    color="primary"
                    checked={Boolean(isFixedFee)}
                    disabled={feeControlsDisabled}
                    onChange={() => {
                      setIsFixedFee((prevValue) => !prevValue);
                      setFormDirty(true);
                    }}
                  />
                }
              />
              <FeeRate isFixedFee={Boolean(isFixedFee)}>
                <CurrencyInput
                  allowNegative={false}
                  value={formFeeRate}
                  size="small"
                  disabled={feeControlsDisabled}
                  onChange={(currency: Currency, amount: number | undefined) => {
                    // A cleared field clears the rate, so `feeRateIsMissing` sees it and the Add or
                    // Update button stays disabled. Coercing to zero here would show an empty field
                    // beside an enabled button and publish a 0 rate - the state this editor's
                    // validation exists to stop.
                    setFormFeeRate(amount === undefined ? undefined : new Money(amount, currency ?? "USD"));
                    setFormDirty(true);
                  }}
                />
                {(getFieldError("fee") || getFieldError("currency")) && (
                  <FormHelperText error>{getFieldError("fee") ?? getFieldError("currency")}</FormHelperText>
                )}
                {/* A fixed fee is not per anything, so it has no unit to ask for. */}
                {!isFixedFee && (
                  <>
                    {/* Read by sight only: the unit field's own label names what follows. */}
                    <PerSymbol aria-hidden="true">
                      <span className="per-slash">/</span>
                      <span className="per-word">per</span>
                    </PerSymbol>
                    <UnitAutocomplete
                      freeSolo
                      disabled={feeControlsDisabled}
                      options={UNIT_OPTIONS}
                      inputValue={formFeeUnit ?? ""}
                      onInputChange={(_event, value) => {
                        setFormFeeUnit(value);
                        setFormDirty(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Unit"
                          size="small"
                          disabled={feeControlsDisabled}
                        />
                      )}
                    />
                  </>
                )}
              </FeeRate>
            </>
          )}
        </FeeFieldset>
        {showsTeamMember && onBrowseForTeamMember && (
          <TeamMemberField>
            <FeeScheduleTeamMemberPickerField
              teamMember={formTeamMember}
              isMasked={category?.teamMemberIsMasked}
              disabled={teamMemberDisabled}
              required={teamMemberIsRequired}
              error={Boolean(getFieldError("team"))}
              helperText={getFieldError("team")}
              roster={teamRoster}
              openedTeamMember={category?.teamMember ?? null}
              onBrowse={onBrowseForTeamMember}
              onChange={handleTeamMemberChanged}
              focusRequest={focusRequestFor(props.focus, "teamMember")}
            />
          </TeamMemberField>
        )}
        {showsTeamMember && !picksTeamMember && (
          <TeamMemberField>
            <Autocomplete<TeamMemberOption>
              disabled={teamMemberDisabled}
              options={teamMemberOptions}
              value={teamMemberOptions.find((option) => option.member.isEqualTo(formTeamMember)) ?? null}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.member.isEqualTo(value.member)}
              onChange={(_event, option) => handleTeamMemberChanged(option?.member ?? null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={category?.teamMemberIsMasked ? "Team Member (restricted)" : "Team Member"}
                  error={Boolean(getFieldError("team"))}
                  helperText={
                    getFieldError("team")
                      ?? (teamMemberIsRequired && teamMemberOptions.length === 0
                        ? "Add a member to the team before setting an hourly rate."
                        : undefined)
                  }
                  required={teamMemberIsRequired}
                  size="small"
                />
              )}
            />
          </TeamMemberField>
        )}
      </FormFields>
      <ButtonContainer>
        {/*
          "Update", and not "Save", with no save icon beside it. This button changes the category in
          the form it is drawn in. It does not write the proposal, the work agreement or the rate card
          that holds the category — a separate Save on the page does that, and the actor must still
          press it. The label and the icon together said that this button was that one (#5657).
        */}
        <Tooltip title={saveDisabledTooltip}>
          <ButtonTooltipTarget>
            <Button
              color="primary"
              variant="contained"
              disabled={saveDisabled}
              onClick={category
                ? () => handleSaveCategoryClicked(category.name)
                : handleAddCategoryClicked}
            >
              {category ? "Update" : "Add"}
            </Button>
          </ButtonTooltipTarget>
        </Tooltip>
        {onCancel && (
          <Button
            color="primary"
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
      </ButtonContainer>
    </FormContainer>
  );
}

